import { useState, useCallback } from 'react';
import { Text, Button, Flex, Box, Spinner } from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { toast } from 'sonner';
import styles from './VerifySnippetDialog.module.scss';

interface DeleteSnippetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snippetTitle: string;
  onDeleteSnippet: () => Promise<void>;
}

export function DeleteSnippetDialog({
  open,
  onOpenChange,
  snippetTitle,
  onDeleteSnippet,
}: DeleteSnippetDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        await onDeleteSnippet();
        toast.success('Snippet deleted successfully');
        onOpenChange(false);
      } catch (error) {
        toast.error('Failed to delete snippet. Please try again.');
        console.error('Error deleting snippet:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isLoading, onDeleteSnippet, onOpenChange]);

  const handleCancel = useCallback(() => {
    if (!isLoading) {
      onOpenChange(false);
    }
  }, [isLoading, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(open) => !isLoading && onOpenChange(open)}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} disabled={isLoading} />
        <Flex direction="column" gap="6" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              Delete Snippet
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Are you sure you want to delete "{snippetTitle}"?
            </Text>
          </Flex>

          <Box p="2" px="3" className={styles.infoMessage}>
            <Flex gap="2" align="start">
              <ExclamationTriangleIcon width={16} height={16} color="orange" />
              <Text size="2" weight="medium" className={styles.infoText}>
                This action cannot be undone. The snippet and all its versions will be permanently deleted.
              </Text>
            </Flex>
          </Box>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="red"
              size="2"
              highContrast
              onClick={handleDelete}
              disabled={isLoading}
              className={styles.verifyButton}
            >
              {isLoading ? (
                <>
                  <Spinner size="1" />
                  <Text size="3">Deleting...</Text>
                </>
              ) : (
                <Text size="3">Delete</Text>
              )}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
